import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Shield, RefreshCw, AlertTriangle, Loader2,
  Users, Activity, ChevronDown, ChevronUp, Power, PauseCircle,
} from "lucide-react";
import { toast } from "sonner";
import LLMUsageMeter from "@/components/LLMUsageMeter";
import { StatusRow } from "@/components/admin/StatusRow";
import { DhanTokenBanner } from "@/components/admin/DhanTokenBanner";
import { LLMSummaryCard } from "@/components/admin/LLMSummaryCard";
import { AICostControls } from "@/components/admin/AICostControls";

/**
 * Centralised admin control panel.
 *
 * One screen for the admin to see every moving part:
 *   • Data feeds   (DHAN, TwelveData, Coinbase, Yahoo, News RSS) + repair
 *   • Background jobs (scanner, topsetups, shock detector, evaluator) + run-now
 *   • Counters (users / signals)
 *   • LLM quick stats
 */
const POLL_MS = 60_000;
const DISMISS_KEY = "wt_admin_panel_collapsed";

export default function AdminControlPanel({ user }) {
  const [collapsed, setCollapsed] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState(null);
  const [llmPaused, setLlmPaused] = useState(false);
  const [switchBusy, setSwitchBusy] = useState(false);
  const [dhanToken, setDhanToken] = useState(null);

  useEffect(() => {
    setCollapsed(!!localStorage.getItem(DISMISS_KEY));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: status } = await api.get("/admin/system-status");
      setData(status);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSwitch = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/llm/switch");
      setLlmPaused(!!data.paused);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadTokenInfo = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/dhan/token-info");
      setDhanToken(data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const toggleLlm = async () => {
    setSwitchBusy(true);
    try {
      const { data: cur } = await api.get("/admin/llm/switch");
      const next = !cur.paused;
      const { data } = await api.post("/admin/llm/switch", { paused: next });
      setLlmPaused(!!data.paused);
      toast[data.paused ? "warning" : "success"](
        data.paused
          ? "AI PAUSED — all LLM credit usage stopped."
          : "AI ACTIVE — signal generation resumed."
      );
      await load();
    } catch (e) {
      toast.error("Could not toggle AI — check console");
      console.error(e);
    } finally {
      setSwitchBusy(false);
    }
  };

  useEffect(() => {
    if (!user?.is_admin) return;
    load();
    loadSwitch();
    loadTokenInfo();
    const id = setInterval(() => { load(); loadSwitch(); loadTokenInfo(); }, POLL_MS);
    return () => clearInterval(id);
  }, [user?.is_admin, load, loadSwitch, loadTokenInfo]);

  const toggleCollapse = () => {
    setCollapsed((c) => {
      const next = !c;
      if (next) localStorage.setItem(DISMISS_KEY, "1");
      else localStorage.removeItem(DISMISS_KEY);
      return next;
    });
  };

  const runAction = async (row) => {
    setBusyAction(row.key);
    try {
      if (row.action_kind === "clear_news_cache") {
        const { data } = await api.post("/admin/cache/news/clear");
        toast.success(`News cache cleared (${data.cleared_inmem} in-mem · ${data.cleared_db} DB)`);
      } else if (row.action_kind === "clear_alerts") {
        const { data } = await api.post("/admin/alerts/clear");
        toast.success(`${data.resolved} alert(s) resolved`);
      } else if (row.action_kind === "run_scanner") {
        toast.info("Scanner running… this may take ~20s");
        const { data } = await api.post("/admin/jobs/scanner/run");
        toast.success(`Scanner finished — recorded ${data.summary?.recorded ?? 0} · skipped ${data.summary?.skipped ?? 0}`);
      } else if (row.action_kind === "evaluate_now") {
        const { data } = await api.post("/admin/accuracy/evaluate-now");
        toast.success(`Evaluator finished — ${data.evaluated ?? 0} signal(s) reviewed`);
      }
      await load();
    } catch (e) {
      toast.error("Action failed — check console");
      console.error(e);
    } finally {
      setBusyAction(null);
    }
  };

  if (!user?.is_admin) return null;

  return (
    <section
      className="border-b border-zinc-900 bg-gradient-to-r from-[#007AFF]/10 via-[#0e0e0e] to-[#0e0e0e] px-4 md:px-6 py-3 relative"
      data-testid="admin-control-panel"
    >
      {/* Header strip */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="shrink-0 bg-[#007AFF]/10 border border-[#007AFF]/40 p-1.5">
          <Shield className="h-4 w-4 text-[#007AFF]" strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[9px] uppercase tracking-[0.3em] text-[#007AFF] font-mono">◢ Admin control</span>
            {data?.healthy === true && (
              <span className="px-1.5 py-0.5 border border-[#22C55E]/40 text-[#22C55E] text-[9px] uppercase tracking-widest font-mono">all systems OK</span>
            )}
            {data?.healthy === false && (
              <span className="px-1.5 py-0.5 border border-[#EF4444]/40 text-[#EF4444] text-[9px] uppercase tracking-widest font-mono inline-flex items-center gap-1">
                <AlertTriangle className="h-2.5 w-2.5" /> attention needed
              </span>
            )}
          </div>
          <div className="font-heading text-base font-bold uppercase tracking-tight truncate">
            {user.name?.split(" ")[0] || "Admin"} · Mission control
          </div>
        </div>

        {/* Quick counters */}
        {data && (
          <div className="hidden md:flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest">
            <div className="text-zinc-500"><Users className="h-3 w-3 inline mr-1" />
              <span className="text-zinc-300">{data.counters.users.total}</span> users · <span className="text-[#22C55E]">{data.counters.users.pro}</span> pro · <span className="text-[#F59E0B]">{data.counters.users.trial}</span> trial
            </div>
            <div className="text-zinc-500"><Activity className="h-3 w-3 inline mr-1" />
              <span className="text-zinc-300">{data.counters.signals.open}</span> open · <span className="text-zinc-300">{data.counters.signals.closed_24h}</span> closed 24h
            </div>
            <div className="text-zinc-500">
              LLM <span className="text-zinc-300">{data.llm.calls_last_hour}</span>/h · <span className="text-zinc-300">{data.llm.calls_last_24h}</span>/24h
            </div>
          </div>
        )}

        <div className="flex items-center gap-1.5 shrink-0">
          <Link
            to="/admin/users"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-[#007AFF]/50 text-[#3B9BFF] hover:bg-[#007AFF]/10 text-[9px] uppercase tracking-widest font-mono transition-colors"
            title="User management — plans, expiries, auto-renew, password resets"
            data-testid="admin-manage-users-link"
          >
            <Users className="h-3 w-3" /> Manage Users
          </Link>
          <button
            onClick={toggleLlm}
            disabled={switchBusy}
            title={llmPaused ? "AI is PAUSED — click to resume signal generation" : "AI is ACTIVE — click to pause ALL LLM credit usage"}
            data-testid="admin-llm-master-switch"
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 border text-[9px] uppercase tracking-widest font-mono transition-colors disabled:opacity-50 ${
              llmPaused
                ? "border-[#EF4444]/60 text-[#EF4444] hover:bg-[#EF4444]/10"
                : "border-[#22C55E]/50 text-[#22C55E] hover:bg-[#22C55E]/10"
            }`}
          >
            {switchBusy ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : llmPaused ? (
              <PauseCircle className="h-3 w-3" />
            ) : (
              <Power className="h-3 w-3" />
            )}
            {llmPaused ? "AI Paused · Resume" : "AI Active · Pause"}
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="text-zinc-500 hover:text-white p-1 disabled:opacity-50"
            title="Refresh"
            data-testid="admin-panel-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button onClick={toggleCollapse} className="text-zinc-500 hover:text-white p-1" title={collapsed ? "Expand" : "Collapse"} data-testid="admin-panel-toggle">
            {collapsed ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      {/* Master-pause banner */}
      {llmPaused && (
        <div
          className="mt-2 flex items-center gap-2 border border-[#EF4444]/40 bg-[#EF4444]/10 px-3 py-1.5"
          data-testid="admin-llm-paused-banner"
        >
          <PauseCircle className="h-3.5 w-3.5 text-[#EF4444] shrink-0" />
          <span className="text-[10px] uppercase tracking-widest text-[#EF4444] font-mono">
            AI paused · zero LLM credits are being spent — background scanner, live signals & news AI are all halted
          </span>
        </div>
      )}

      <DhanTokenBanner dhanToken={dhanToken} />

      {/* Expanded body */}
      {!collapsed && data && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Data feeds */}
          <div className="bg-[#121212] border border-zinc-800 p-3" data-testid="admin-feeds-card">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2">Data feeds</div>
            <div>
              {data.feeds.map((f) => (
                <StatusRow key={f.key} row={f} onAction={runAction} busy={busyAction === f.key} />
              ))}
            </div>
          </div>

          {/* Background jobs */}
          <div className="bg-[#121212] border border-zinc-800 p-3" data-testid="admin-jobs-card">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2">Background jobs</div>
            <div>
              {data.jobs.map((j) => (
                <StatusRow key={j.key} row={j} onAction={runAction} busy={busyAction === j.key} />
              ))}
            </div>
          </div>

          <LLMSummaryCard llm={data.llm} />

          {/* Token spend / cost meter — 4th column */}
          <LLMUsageMeter />
        </div>
      )}

      {/* AI Cost Controls — all LLM-spend rules, admin-tunable */}
      {!collapsed && <AICostControls />}

      {/* Collapsed-state mobile counters */}
      {collapsed && data && (
        <div className="mt-2 md:hidden text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          {data.counters.users.total} users · {data.counters.signals.open} open · {data.llm.calls_last_24h} llm/24h
        </div>
      )}
    </section>
  );
}
